import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Upload, Download, FileText, CheckCircle, AlertCircle, RefreshCw, Loader2, X, MapPin } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new: { label: 'Nouveau', variant: 'default', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  valid: { label: 'Valide', variant: 'default', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  duplicate: { label: 'Doublon', variant: 'outline', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  error: { label: 'Erreur', variant: 'destructive', className: 'bg-red-50 text-red-700 border-red-200' },
};

export default function CSVImportModal({ open, onOpenChange, type, columns, onSuccess, customResultRender }) {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [geocodeJob, setGeocodeJob] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const reset = () => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setStats(null);
    setResult(null);
    setGeocodeJob(null);
    setGeocoding(false);
    if (pollRef.current) clearInterval(pollRef.current);
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleFileSelect = async (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!selected.name.endsWith('.csv')) {
      toast({ title: 'Format invalide', description: 'Veuillez sélectionner un fichier .csv', variant: 'destructive' });
      return;
    }
    setFile(selected);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', selected);
      const res = await axios.post(`${API}/import/${type}/preview`, formData);
      setPreview(res.data.preview);
      setStats(res.data.stats);
      setStep('preview');
    } catch (err) {
      toast({ title: 'Erreur de lecture', description: err.response?.data?.detail || 'Impossible de lire le fichier', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/import/${type}/confirm`, formData);
      setResult(res.data);
      setStep('result');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || "Erreur lors de l'import", variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/import/${type}/template`, '_blank');
  };

  const typeLabel = type === 'drivers' ? 'Chauffeurs' : type === 'vehicles' ? 'Véhicules' : type === 'partners' ? 'Transporteurs' : type === 'clients' ? 'Clients' : type === 'external-references' ? 'Références client' : 'Opérations';

  const handleStartGeocode = async () => {
    if (!result?.created_ids?.length) return;
    setGeocoding(true);
    try {
      const res = await axios.post(`${API}/import/geocode-batch`, { operation_ids: result.created_ids });
      const jobId = res.data.job_id;
      setGeocodeJob({ ...res.data, status: 'running', processed: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const poll = await axios.get(`${API}/import/geocode-batch/${jobId}`);
          setGeocodeJob(poll.data);
          if (poll.data.status === 'completed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setGeocoding(false);
          }
        } catch { /* ignore poll errors */ }
      }, 2000);
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible de lancer le géocodage', variant: 'destructive' });
      setGeocoding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-emerald-600" />
            Importer {typeLabel} (CSV)
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Sélectionnez un fichier CSV à importer'}
            {step === 'preview' && 'Vérifiez les données avant de confirmer'}
            {step === 'result' && "Résultat de l'import"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-emerald-300 transition-colors cursor-pointer"
                onClick={() => fileRef.current?.click()}
                data-testid="csv-dropzone"
              >
                {loading ? (
                  <Loader2 className="w-10 h-10 mx-auto text-emerald-500 animate-spin" />
                ) : (
                  <>
                    <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                    <p className="text-sm font-medium text-gray-700">Cliquez pour sélectionner un fichier CSV</p>
                    <p className="text-xs text-gray-400 mt-1">Séparateur : point-virgule (;) ou virgule (,)</p>
                  </>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="csv-file-input"
                />
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="w-full" data-testid="csv-download-template">
                <Download className="w-4 h-4 mr-2" />
                Télécharger le modèle CSV
              </Button>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && preview && (
            <div className="space-y-3">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-gray-900">{stats?.total || 0}</p>
                  <p className="text-[10px] text-gray-500 uppercase">Total</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-emerald-700">{stats?.valid || 0}</p>
                  <p className="text-[10px] text-emerald-600 uppercase">{type === 'external-references' ? 'À appliquer' : 'Nouveaux'}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-amber-700">{stats?.duplicate || 0}</p>
                  <p className="text-[10px] text-amber-600 uppercase">Doublons</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-red-700">{stats?.error || 0}</p>
                  <p className="text-[10px] text-red-600 uppercase">Erreurs</p>
                </div>
              </div>

              {/* Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-10 text-xs">#</TableHead>
                      {columns.map(col => (
                        <TableHead key={col.key} className="text-xs">{col.label}</TableHead>
                      ))}
                      <TableHead className="text-xs">Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((row, idx) => {
                      const cfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.new;
                      return (
                        <TableRow key={idx} className={row.status === 'error' ? 'bg-red-50/50' : ''}>
                          <TableCell className="text-xs text-gray-400">{row.line}</TableCell>
                          {columns.map(col => (
                            <TableCell key={col.key} className="text-xs">{row[col.key] || '-'}</TableCell>
                          ))}
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${cfg.className}`}>{row.action}</Badge>
                              {row.errors?.length > 0 && (
                                <span className="text-[10px] text-red-500">{row.errors.join(', ')}</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Step 3: Result */}
          {step === 'result' && result && (
            <div className="space-y-4 py-4">
              {customResultRender ? customResultRender(result) : (
                <>
                  <div className="text-center">
                    <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                    <p className="text-lg font-semibold text-gray-900">Import terminé</p>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-emerald-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-700">{result.created ?? 0}</p>
                      <p className="text-xs text-emerald-600">Créés</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">{result.updated}</p>
                      <p className="text-xs text-blue-600">Mis à jour</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-red-700">{result.errors?.length || 0}</p>
                      <p className="text-xs text-red-600">Erreurs</p>
                    </div>
                  </div>
                  {result.errors?.length > 0 && (
                    <div className="border border-red-100 rounded-lg p-3 space-y-1">
                      <p className="text-xs font-medium text-red-700">Détail des erreurs :</p>
                      {result.errors.map((err, i) => {
                        if (typeof err === 'string') {
                          return <p key={i} className="text-xs text-red-600">{err}</p>;
                        }
                        const details = Array.isArray(err.errors) ? err.errors.join(', ') : (err.message || err.error || '');
                        return <p key={i} className="text-xs text-red-600">Ligne {err.line}: {details}</p>;
                      })}
                    </div>
                  )}

              {/* Partners import details */}
              {type === 'partners' && (result.vehicles_created > 0 || result.drivers_created > 0 || result.skipped_duplicates > 0) && (
                <div className="border border-emerald-100 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium text-emerald-800">Détail de l'import :</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-blue-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-blue-700">{result.vehicles_created || 0}</p>
                      <p className="text-[10px] text-blue-600">Véhicules créés</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-purple-700">{result.drivers_created || 0}</p>
                      <p className="text-[10px] text-purple-600">Chauffeurs créés</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-amber-700">{result.skipped_duplicates || 0}</p>
                      <p className="text-[10px] text-amber-600">Doublons ignorés</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Geocoding for operations */}
              {type === 'operations' && result.created_ids?.length > 0 && (
                <div className="border border-blue-100 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <p className="text-sm font-medium text-blue-800">Géocodage des adresses</p>
                  </div>                  {!geocodeJob ? (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">
                        Calculer automatiquement les coordonnées GPS et distances pour les {result.created_ids.length} opération(s) importée(s).
                      </p>
                      <Button
                        size="sm"
                        onClick={handleStartGeocode}
                        disabled={geocoding}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="csv-geocode-btn"
                      >
                        {geocoding ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MapPin className="w-3 h-3 mr-1" />}
                        Géocoder
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">
                          {geocodeJob.status === 'completed' ? 'Terminé' : 'En cours...'}
                        </span>
                        <span className="font-medium">{geocodeJob.processed}/{geocodeJob.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${geocodeJob.total > 0 ? (geocodeJob.processed / geocodeJob.total) * 100 : 0}%` }}
                        />
                      </div>
                      {geocodeJob.status === 'completed' && (
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          <div className="bg-blue-50 rounded-lg p-2 text-center">
                            <p className="text-sm font-bold text-blue-700">{geocodeJob.geocoded}</p>
                            <p className="text-[10px] text-blue-500">Géocodés</p>
                          </div>
                          <div className="bg-emerald-50 rounded-lg p-2 text-center">
                            <p className="text-sm font-bold text-emerald-700">{geocodeJob.routed}</p>
                            <p className="text-[10px] text-emerald-500">Distances</p>
                          </div>
                          <div className="bg-red-50 rounded-lg p-2 text-center">
                            <p className="text-sm font-bold text-red-700">{geocodeJob.errors}</p>
                            <p className="text-[10px] text-red-500">Erreurs</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {step === 'upload' && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={reset}>
                <RefreshCw className="w-3 h-3 mr-1" /> Recommencer
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={loading || (stats?.valid === 0 && stats?.duplicate === 0)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="csv-confirm-import"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Importer {(stats?.valid || 0) + (stats?.duplicate || 0)} ligne(s)
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button onClick={() => { onOpenChange(false); reset(); }} className="bg-emerald-600 hover:bg-emerald-700">
              Fermer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
